/**
 * AdminBookingDetail Component
 * Full detail page for viewing and managing a booking
 * URL: /beu-admin-x7k9/bookings/:bookingId
 *
 * Operations Lead can:
 * - View complete booking details
 * - Change booking status
 * - See client/professional/salon info
 * - View payment details
 * - Track who made changes (audit trail)
 */

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import AdminWrapper from './AdminWrapper';
import { adminService } from '../../_services/admin.service';
import './Admin.css';

// Booking status mapping
const BOOKING_STATUS = {
    0: { label: 'Pending', className: 'admin-badge-warning', color: '#ef6c00' },
    1: { label: 'Booked', className: 'admin-badge-info', color: '#1565c0' },
    2: { label: 'Confirmed', className: 'admin-badge-success', color: '#2e7d32' },
    3: { label: 'Cancelled', className: 'admin-badge-danger', color: '#c62828' },
    4: { label: 'Completed', className: 'admin-badge-success', color: '#2e7d32' },
    5: { label: 'Rescheduled', className: 'admin-badge-warning', color: '#ef6c00' },
    6: { label: 'No Show', className: 'admin-badge-danger', color: '#c62828' }
};

// Payment type mapping
const PAYMENT_TYPES = {
    1: 'Pay by App',
    2: 'Card',
    3: 'Cash',
    4: 'Other',
    5: 'BEU Card Reader',
    10: 'Card on File',
    11: 'Voucher',
    12: 'Payment Link',
    13: 'Klarna',
    14: 'Clearpay'
};

class AdminBookingDetail extends Component {
    constructor(props) {
        super(props);
        this.state = {
            booking: null,
            loading: true,
            error: null,
            editingStatus: false,
            newStatus: null,
            saving: false,
            saveSuccess: false,
            saveError: null
        };
    }

    componentDidMount() {
        this.fetchBooking();
    }

    fetchBooking = async () => {
        const { id } = this.props.match.params;
        this.setState({ loading: true, error: null });

        try {
            const response = await adminService.getBookingById(id);
            if (response.statusCode === 1) {
                this.setState({
                    booking: response.responseData,
                    newStatus: response.responseData.bookingStatus,
                    loading: false
                });
            } else {
                this.setState({ error: 'Failed to load booking', loading: false });
            }
        } catch (error) {
            console.error('Error fetching booking:', error);
            this.setState({ error: 'Failed to load booking', loading: false });
        }
    }

    handleStatusChange = async (newStatus) => {
        const { booking } = this.state;
        const statusLabel = BOOKING_STATUS[newStatus]?.label || 'Unknown';

        if (!window.confirm(`Change booking status to "${statusLabel}"?\n\nThis action will be logged.`)) {
            return;
        }

        this.setState({ saving: true, saveError: null, saveSuccess: false });

        try {
            const response = await adminService.updateBooking(booking._id, { bookingStatus: newStatus });
            if (response.statusCode === 1) {
                this.setState({
                    booking: response.responseData,
                    newStatus: response.responseData.bookingStatus,
                    saving: false,
                    editingStatus: false,
                    saveSuccess: true
                });
                setTimeout(() => this.setState({ saveSuccess: false }), 3000);
            } else {
                this.setState({ saveError: 'Failed to update status', saving: false });
            }
        } catch (error) {
            console.error('Error updating booking:', error);
            this.setState({ saveError: 'Failed to update status', saving: false });
        }
    }

    getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }

    formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    }

    formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    formatTime = (slots) => {
        if (!slots || slots.length === 0) return 'N/A';
        // bookingSlot is [startMinutes, endMinutes] from midnight
        const startSlot = slots[0];
        const endSlot = slots[1];
        const startHours = Math.floor(startSlot / 60);
        const startMinutes = startSlot % 60;
        const endHours = Math.floor(endSlot / 60);
        const endMinutes = endSlot % 60;
        return `${startHours.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')} - ${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
    }

    formatCurrency = (amount, symbol) => {
        if (amount === undefined || amount === null) return 'N/A';
        return `${symbol || '£'}${parseFloat(amount).toFixed(2)}`;
    }

    calculateTotalAmount = (booking) => {
        let total = 0;
        if (booking.totalAmount) total += parseFloat(booking.totalAmount) || 0;
        if (booking.totalProductPrice) total += parseFloat(booking.totalProductPrice) || 0;
        if (booking.totalVoucherPrice) total += parseFloat(booking.totalVoucherPrice) || 0;
        return total;
    }

    getClientInfo = (booking) => {
        if (booking.userId && typeof booking.userId === 'object') {
            return booking.userId;
        }
        return {
            name: booking.clientName,
            email: booking.clientEmail,
            phoneNumber: booking.clientPhone,
            localAvatar: booking.clientAvatar
        };
    }

    getHairdresserInfo = (booking) => {
        if (booking.hairdresserId && typeof booking.hairdresserId === 'object') {
            return booking.hairdresserId;
        }
        return {
            name: booking.hairdresserName,
            email: booking.hairdresserEmail
        };
    }

    getSalonInfo = (booking) => {
        if (booking.salonId && typeof booking.salonId === 'object') {
            return booking.salonId;
        }
        return {
            name: booking.salonName
        };
    }

    getPaymentMethods = (transaction) => {
        if (!transaction || transaction.length === 0) return [];
        return transaction.map(t => ({
            type: PAYMENT_TYPES[t.paymentType] || 'Unknown',
            amount: t.amount
        }));
    }

    render() {
        const { booking, loading, error, editingStatus, saving, saveSuccess, saveError } = this.state;

        if (loading) {
            return (
                <AdminWrapper>
                    <div style={{ padding: '60px', textAlign: 'center' }}>
                        <div className="admin-loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                        <p style={{ color: '#666' }}>Loading booking...</p>
                    </div>
                </AdminWrapper>
            );
        }

        if (error || !booking) {
            return (
                <AdminWrapper>
                    <div style={{ padding: '60px', textAlign: 'center' }}>
                        <p style={{ color: '#ff6b6b', marginBottom: '16px' }}>{error || 'Booking not found'}</p>
                        <Link to="/beu-admin-x7k9/bookings" className="admin-btn admin-btn-secondary">
                            Back to Bookings
                        </Link>
                    </div>
                </AdminWrapper>
            );
        }

        const client = this.getClientInfo(booking);
        const hairdresser = this.getHairdresserInfo(booking);
        const salon = this.getSalonInfo(booking);
        const totalAmount = this.calculateTotalAmount(booking);
        const statusInfo = BOOKING_STATUS[booking.bookingStatus] || { label: 'Unknown', className: 'admin-badge-secondary' };
        const payments = this.getPaymentMethods(booking.transaction);

        return (
            <AdminWrapper>
                {/* Breadcrumb */}
                <div style={{ marginBottom: '24px' }}>
                    <Link to="/beu-admin-x7k9/bookings" style={{ color: '#666', textDecoration: 'none', fontSize: '14px' }}>
                        ← Back to Bookings
                    </Link>
                </div>

                {/* Success/Error Messages */}
                {saveSuccess && (
                    <div style={{
                        padding: '12px 16px',
                        background: '#e8f5e9',
                        borderRadius: '8px',
                        marginBottom: '24px',
                        color: '#2e7d32',
                        fontWeight: '500'
                    }}>
                        ✓ Status updated successfully
                    </div>
                )}
                {saveError && (
                    <div style={{
                        padding: '12px 16px',
                        background: '#ffebee',
                        borderRadius: '8px',
                        marginBottom: '24px',
                        color: '#c62828',
                        fontWeight: '500'
                    }}>
                        ✕ {saveError}
                    </div>
                )}

                {/* Status Banner */}
                <div style={{
                    padding: '24px',
                    borderRadius: '16px',
                    background: statusInfo.color + '15',
                    borderLeft: `4px solid ${statusInfo.color}`,
                    marginBottom: '24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px'
                }}>
                    <div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Booking Status</div>
                        <span className={`admin-badge ${statusInfo.className}`} style={{ fontSize: '16px', padding: '8px 16px' }}>
                            {statusInfo.label}
                        </span>
                    </div>
                    {!editingStatus ? (
                        <button
                            className="admin-btn admin-btn-primary"
                            onClick={() => this.setState({ editingStatus: true })}
                        >
                            Change Status
                        </button>
                    ) : (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {Object.entries(BOOKING_STATUS).map(([status, info]) => (
                                <button
                                    key={status}
                                    className={`admin-btn admin-btn-sm ${parseInt(status) === booking.bookingStatus ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
                                    onClick={() => this.handleStatusChange(parseInt(status))}
                                    disabled={saving || parseInt(status) === booking.bookingStatus}
                                >
                                    {info.label}
                                </button>
                            ))}
                            <button
                                className="admin-btn admin-btn-sm admin-btn-secondary"
                                onClick={() => this.setState({ editingStatus: false })}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>

                {/* Main Content */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
                    {/* Left Column */}
                    <div>
                        {/* Appointment Details */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">Appointment Details</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: '24px'
                                }}>
                                    <div>
                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Date</div>
                                        <div style={{ fontSize: '18px', fontWeight: '600' }}>{this.formatDate(booking.bookingDate)}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Time</div>
                                        <div style={{ fontSize: '18px', fontWeight: '600' }}>{this.formatTime(booking.bookingSlot)}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Services */}
                        {booking.servicesBooked && booking.servicesBooked.length > 0 && (
                            <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                                <div className="admin-table-header">
                                    <h3 className="admin-table-title">Services ({booking.servicesBooked.length})</h3>
                                </div>
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Service</th>
                                            <th>Duration</th>
                                            <th style={{ textAlign: 'right' }}>Price</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {booking.servicesBooked.map((service, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <div style={{ fontWeight: '500' }}>{service.serviceName}</div>
                                                    {service.serviceDescription && (
                                                        <div style={{ fontSize: '12px', color: '#666' }}>{service.serviceDescription}</div>
                                                    )}
                                                </td>
                                                <td>{service.serviceTime ? `${service.serviceTime} mins` : 'N/A'}</td>
                                                <td style={{ textAlign: 'right', fontWeight: '500' }}>
                                                    {this.formatCurrency(service.servicePrice, booking.currencySymbol)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="2" style={{ fontWeight: '600' }}>Services Subtotal</td>
                                            <td style={{ textAlign: 'right', fontWeight: '600' }}>
                                                {this.formatCurrency(booking.totalAmount, booking.currencySymbol)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}

                        {/* Products */}
                        {booking.products && booking.products.length > 0 && (
                            <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                                <div className="admin-table-header">
                                    <h3 className="admin-table-title">Products ({booking.products.length})</h3>
                                </div>
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th style={{ textAlign: 'right' }}>Price</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {booking.products.map((product, index) => (
                                            <tr key={index}>
                                                <td style={{ fontWeight: '500' }}>{product.productName}</td>
                                                <td style={{ textAlign: 'right', fontWeight: '500' }}>
                                                    {this.formatCurrency(product.retailPrice, booking.currencySymbol)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td style={{ fontWeight: '600' }}>Products Subtotal</td>
                                            <td style={{ textAlign: 'right', fontWeight: '600' }}>
                                                {this.formatCurrency(booking.totalProductPrice, booking.currencySymbol)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}

                        {/* Vouchers */}
                        {booking.vouchers && booking.vouchers.length > 0 && (
                            <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                                <div className="admin-table-header">
                                    <h3 className="admin-table-title">Vouchers Sold ({booking.vouchers.length})</h3>
                                </div>
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Voucher Code</th>
                                            <th>Type</th>
                                            <th style={{ textAlign: 'right' }}>Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {booking.vouchers.map((voucher, index) => (
                                            <tr key={index}>
                                                <td style={{ fontWeight: '500' }}>{voucher.code || 'N/A'}</td>
                                                <td>{voucher.type || 'Cash'}</td>
                                                <td style={{ textAlign: 'right', fontWeight: '500' }}>
                                                    {this.formatCurrency(voucher.voucherPrice, booking.currencySymbol)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="2" style={{ fontWeight: '600' }}>Vouchers Subtotal</td>
                                            <td style={{ textAlign: 'right', fontWeight: '600' }}>
                                                {this.formatCurrency(booking.totalVoucherPrice, booking.currencySymbol)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}

                        {/* Payment Summary */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">Payment Summary</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                <div className="admin-detail-grid">
                                    {booking.totalAmount > 0 && (
                                        <div className="admin-detail-row">
                                            <label>Services</label>
                                            <span>{this.formatCurrency(booking.totalAmount, booking.currencySymbol)}</span>
                                        </div>
                                    )}
                                    {booking.totalProductPrice > 0 && (
                                        <div className="admin-detail-row">
                                            <label>Products</label>
                                            <span>{this.formatCurrency(booking.totalProductPrice, booking.currencySymbol)}</span>
                                        </div>
                                    )}
                                    {booking.totalVoucherPrice > 0 && (
                                        <div className="admin-detail-row">
                                            <label>Vouchers</label>
                                            <span>{this.formatCurrency(booking.totalVoucherPrice, booking.currencySymbol)}</span>
                                        </div>
                                    )}
                                    {booking.tipAmount > 0 && (
                                        <div className="admin-detail-row">
                                            <label>Tip</label>
                                            <span>{this.formatCurrency(booking.tipAmount, booking.currencySymbol)}</span>
                                        </div>
                                    )}
                                    <div className="admin-detail-row" style={{ borderTop: '2px solid #1a1a1a', paddingTop: '16px', marginTop: '8px' }}>
                                        <label style={{ fontWeight: '700', fontSize: '16px' }}>Total</label>
                                        <span style={{ fontWeight: '700', fontSize: '20px' }}>
                                            {this.formatCurrency(totalAmount + (booking.tipAmount || 0), booking.currencySymbol)}
                                        </span>
                                    </div>
                                </div>

                                {/* Payment Methods */}
                                {payments.length > 0 && (
                                    <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #f0f0f0' }}>
                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px', textTransform: 'uppercase', fontWeight: '600' }}>Payment Methods</div>
                                        {payments.map((payment, index) => (
                                            <div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                                <span>{payment.type}</span>
                                                <span style={{ fontWeight: '500' }}>
                                                    {this.formatCurrency(payment.amount, booking.currencySymbol)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Deposit Info */}
                                {booking.isDeposit === 1 && (
                                    <div style={{ marginTop: '16px', padding: '16px', background: '#fff3e0', borderRadius: '8px' }}>
                                        <div style={{ fontWeight: '600', marginBottom: '4px' }}>Deposit Required</div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>Amount: {this.formatCurrency(booking.depositAmount, booking.currencySymbol)}</span>
                                            <span className={`admin-badge ${booking.isDepositPay ? 'admin-badge-success' : 'admin-badge-warning'}`}>
                                                {booking.isDepositPay ? 'Paid' : 'Pending'}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div>
                        {/* Client Card */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">Client</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                                    {client.localAvatar ? (
                                        <img src={client.localAvatar} alt={client.name} style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover' }} />
                                    ) : (
                                        <div style={{
                                            width: '56px', height: '56px', borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '20px', fontWeight: '600'
                                        }}>
                                            {this.getInitials(client.name)}
                                        </div>
                                    )}
                                    <div>
                                        <div style={{ fontWeight: '600', fontSize: '16px' }}>{client.name || 'Unknown'}</div>
                                        <div style={{ color: '#666', fontSize: '14px' }}>{client.email || ''}</div>
                                    </div>
                                </div>
                                {client.phoneNumber && (
                                    <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px', marginBottom: '12px' }}>
                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Phone</div>
                                        <div style={{ fontWeight: '500' }}>{client.phoneNumber}</div>
                                    </div>
                                )}
                                {booking.userId && typeof booking.userId === 'object' && (
                                    <Link
                                        to={`/beu-admin-x7k9/users/${booking.userId._id}`}
                                        className="admin-btn admin-btn-secondary"
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        View Client Profile →
                                    </Link>
                                )}
                            </div>
                        </div>

                        {/* Professional Card */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">Professional</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{ fontWeight: '600', fontSize: '16px' }}>{hairdresser.name || 'Unknown'}</div>
                                    <div style={{ color: '#666', fontSize: '14px' }}>{hairdresser.email || ''}</div>
                                </div>
                                {booking.hairdresserId && typeof booking.hairdresserId === 'object' && (
                                    <Link
                                        to={`/beu-admin-x7k9/professionals/${booking.hairdresserId._id}`}
                                        className="admin-btn admin-btn-secondary"
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        View Professional →
                                    </Link>
                                )}
                            </div>
                        </div>

                        {/* Salon Card */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">Business</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{ fontWeight: '600', fontSize: '16px' }}>{salon.name || salon.salonName || 'Unknown'}</div>
                                    {salon.address && (
                                        <div style={{ color: '#666', fontSize: '14px', marginTop: '4px' }}>
                                            {typeof salon.address === 'object'
                                                ? `${salon.address.addressLine1 || ''}, ${salon.address.city || ''}`
                                                : salon.address}
                                        </div>
                                    )}
                                </div>
                                {booking.salonId && typeof booking.salonId === 'object' && (
                                    <Link
                                        to={`/beu-admin-x7k9/businesses/${booking.salonId._id}`}
                                        className="admin-btn admin-btn-secondary"
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        View Business →
                                    </Link>
                                )}
                            </div>
                        </div>

                        {/* System Info */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">System Information</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                <div className="admin-detail-grid">
                                    <div className="admin-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                        <label>Booking ID</label>
                                        <span className="admin-id-text">{booking._id}</span>
                                    </div>
                                    <div className="admin-detail-row">
                                        <label>Booking Type</label>
                                        <span className={`admin-badge ${booking.isOnlineBooking ? 'admin-badge-info' : 'admin-badge-secondary'}`}>
                                            {booking.isOnlineBooking ? 'Online' : 'In-Person'}
                                        </span>
                                    </div>
                                    <div className="admin-detail-row">
                                        <label>Created</label>
                                        <span>{this.formatDateTime(booking.createdTime)}</span>
                                    </div>
                                    <div className="admin-detail-row">
                                        <label>Last Updated</label>
                                        <span>{this.formatDateTime(booking.updatedTime)}</span>
                                    </div>
                                    {booking.bookingCompleteDate && (
                                        <div className="admin-detail-row">
                                            <label>Completed</label>
                                            <span>{this.formatDateTime(booking.bookingCompleteDate)}</span>
                                        </div>
                                    )}
                                    {booking.cancelledDate && (
                                        <div className="admin-detail-row">
                                            <label>Cancelled</label>
                                            <span>{this.formatDateTime(booking.cancelledDate)}</span>
                                        </div>
                                    )}
                                    {booking.noShowDate && (
                                        <div className="admin-detail-row">
                                            <label>No Show Marked</label>
                                            <span>{this.formatDateTime(booking.noShowDate)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="admin-table-container">
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">Quick Actions</h3>
                            </div>
                            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {booking.bookingStatus !== 4 && booking.bookingStatus !== 3 && (
                                    <button
                                        className="admin-btn"
                                        style={{ width: '100%', justifyContent: 'center', background: '#2e7d32', color: '#fff' }}
                                        onClick={() => this.handleStatusChange(4)}
                                        disabled={saving}
                                    >
                                        Mark as Completed
                                    </button>
                                )}
                                {booking.bookingStatus !== 3 && (
                                    <button
                                        className="admin-btn admin-btn-danger"
                                        style={{ width: '100%', justifyContent: 'center' }}
                                        onClick={() => this.handleStatusChange(3)}
                                        disabled={saving}
                                    >
                                        Cancel Booking
                                    </button>
                                )}
                                {booking.bookingStatus !== 6 && booking.bookingStatus !== 4 && booking.bookingStatus !== 3 && (
                                    <button
                                        className="admin-btn admin-btn-secondary"
                                        style={{ width: '100%', justifyContent: 'center' }}
                                        onClick={() => this.handleStatusChange(6)}
                                        disabled={saving}
                                    >
                                        Mark as No Show
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </AdminWrapper>
        );
    }
}

export default AdminBookingDetail;
